/**
 * Unified Data Structure for RTS-SAREK Project
 * 프로젝트의 모든 데이터를 하나로 통합한 구조
 */

// =============================================
// 1. 기본 타입 정의
// =============================================

/** 온도/습도 조건 */
export interface ConditionData {
  temperature: string  // 온도 (℃)
  humidity: string     // 습도 (%)
}

/** 여름/겨울 조건 */
export interface SeasonalCondition {
  summer: ConditionData
  winter: ConditionData
}

/** 외기설계조건 정보 */
export interface OutdoorCondition extends SeasonalCondition {
  code: string              // A, B, C, D, E, F
  region: string           // 지역명
  summer: ConditionData & {
    dailyRange?: string    // 일교차 (℃) - 여름철만
  }
}

// =============================================
// 2. 프로젝트 정보 (확장된 ProjectData)
// =============================================

export interface ProjectInfo {
  // 기본 정보
  id: string
  projectName: string
  buildingName: string
  location: string
  buildingType: string
  totalFloors: string
  basementFloors: string
  buildingArea: string
  
  // 외기조건 설정
  outdoorConditionCode: string        // 선택된 외기조건 코드 (A~F)
  altitude: string                    // 고도 (m) - ASHRAE 계산용
  
  // 커스텀 외기조건 (코드별 오버라이드)
  customOutdoorConditions?: {
    [code: string]: {
      summer: ConditionData & { dailyRange?: string }
      winter: ConditionData
    }
  }
  
  // 실내조건 설정
  summerIndoorTemp: string
  summerIndoorHumidity: string
  winterIndoorTemp: string
  winterIndoorHumidity: string
  
  // 메타데이터
  createdAt: string
  updatedAt: string
}

// =============================================
// 3. AHU 선정 폼 데이터
// =============================================

export interface AHUFormData {
  // 기본 정보
  ahuNo: string
  systemName: string
  quantity: string
  location: string
  
  // (1) 공간 정보
  conditionedArea: string       // 공조면적 (m²)
  ceilingHeight: string         // 층고 (m)
  occupantCount: string         // 인원수 (명)
  
  // (2) 공조 부하
  sensibleCoolingLoad: string      // 냉방 현열 부하(W)
  latentCoolingLoad: string        // 냉방 잠열 부하(W)
  ventilationLightingLoad: string // 환기 조명부하(W)
  heatingLoad: string           // 난방 부하(W)
  raLightingLoadMode: 'included' | 'separate' | 'no' // RA Lighting Load 모드
  
  // (3) 장비 기준
  outdoorDesignCondition: string    // 외기설계조건 코드
  outdoorTimeApplication: 'standard' | 'peak'  // 외기 적용 시각 방식 ('표준 적용' 또는 '피크 시각 적용')
  outdoorApplicationTime: string    // 냉방 외기 적용시각 (시) - 피크 시각 적용 모드에서 사용
  systemType: string               // 시스템 타입
  fanType: string                  // 팬 타입
  
  // (4) 코일 설정
  coolingCoilType: string
  reheatingCoilType: string
  preheatingCoilType: string
  heatingCoilType: string
  humidifierType: string
  
  // (5) 필터 설정
  preFilterType: string
  mediumFilterType: string
  
  // (6) 배열 회수기
  heatRecoveryType: string         // None, S.H, T.H
  heatRecoveryCoolingEfficiency: string // 냉방효율 (%)
  heatRecoveryHeatingEfficiency: string // 난방효율 (%)
  
  // (7) 급기/환기 비율
  saAirflowRatio: string          // 급기 풍량 비율 (%)
  oaAirflowRatio: string          // 외기 풍량 비율 (%)
  eaAirflowRatio: string          // 배기 풍량 비율 (%)
  raAirflowRatio: string          // 환기 풍량 비율 (%)
  
  // (8) 팬 설정 (Fan Selection에서 연동)
  sfLoadPercentage: string        // 급기팬 적용비율 (%)
  rfLoadPercentage: string        // 환기팬 적용비율 (%)
  
  // (9) 추가 옵션
  silencerType: string
  damperType: string
  insulation: boolean
  weatherCover: boolean
  drainPan: boolean
  accessDoor: boolean
  
  // (10) 메모 및 기타
  notes?: string
}

// =============================================
// 4. 팬 선정 데이터
// =============================================

/** 급기/환기팬 개별 데이터 */
export interface FanData {
  // 기본 선정 정보
  position: string               // Draw(I), Draw(O), Blow(I), Blow(O) 또는 Inside, Outside, None
  loadPercentage: string         // 적용 비율 (%)
  equipmentNumber: string        // 장비번호
  
  // 계산된 성능 데이터
  airflow: string               // 풍량 (CMH)
  staticPressure: string        // 정압 (Pa)
  motorPower: string            // 전동기 (kW)
  efficiency: string            // 효율 (%)
  rpm: string                   // RPM
  safetyFactor: string          // 모터 안전율 (%)
  
  // 덕트 압력손실 데이터
  ductPressureLoss: {
    supplyDuctFriction: string         // 급기덕트 마찰손실
    supplyDuctLength: string           // 급기덕트 길이
    returnDuctFriction: string         // 환기덕트 마찰손실
    returnDuctLength: string           // 환기덕트 길이
    supplyAirDevicePressureLoss: string // 급기 에어디바이스
    returnAirDevicePressureLoss: string // 환기 에어디바이스
    preFilterPressureLoss: string      // 프리필터
    mediumFilterPressureLoss: string   // 중성능필터
    heatingCoilPressureLoss: string    // 가열코일
    coolingCoilPressureLoss: string    // 냉각코일
    humidifierPressureLoss: string     // 가습기
    soundAttenuatorPressureLoss: string // 소음기
    damperPressureLoss: string         // 댐퍼
    otherPressureLoss: string          // 기타
    total: string                      // 총 압력손실
  }
}

/** 팬 선정 전체 데이터 */
export interface FanSelectionData {
  // 선정 기준
  criteria: {
    saFanPosition: string          // SA Fan 위치
    saFanRatio: number            // SA Fan 비율
    raFanPosition: string         // RA Fan 위치  
    raFanRatio: number           // RA Fan 비율
    safetyMode: string           // 안전율 모드
    rfEquipment: string          // RF 장비번호
    safetyPercentage1: number    // 안전율 1 (%)
    safetyPercentage2: number    // 안전율 2 (%)
  }
  
  // 팬 데이터
  supplyFan: FanData            // 급기팬 (SA)
  returnFan: FanData            // 환기팬 (RA)
  
  // 정압 계산 결과
  sfStaticPressure: {
    calculated: string           // 계산값
    selected: string            // 선정값
    total: string               // 합계
  }
  
  rfStaticPressure: {
    calculated: string
    selected: string
    total: string
  }
  
  // 메타데이터
  lastCalculated?: string       // 마지막 계산 시간
  notes?: string               // 메모
}

// =============================================
// 5. AHU 전체 데이터 (Project AHU)
// =============================================

export interface AHUData {
  // 메타데이터
  id: string                    // AHU 고유 ID
  projectId: string            // 소속 프로젝트 ID
  
  // 기본 정보 (AHUFormData에서 중복)
  ahuNo: string
  systemName: string
  quantity: string
  location: string
  
  // 폼 데이터 (완전한 타입 지정)
  formData: AHUFormData
  
  // 팬 선정 데이터 (연동)
  fanSelectionData?: FanSelectionData
  
  // 계산 결과 캐싱
  calculationResults?: {
    lastUpdated: string
    mixedAirCondition: any      // 혼합공기 조건
    coolingCoilResults: any     // 냉각코일 결과
    heatingCoilResults: any     // 가열코일 결과
    psychrometricData: any      // 습공기선도 데이터
  }

  // 습공기 선도 데이터 (풍량선정 탭에서 계산됨)
  psychroChartData?: {
    points: any[]
    lines: any[]
    minTemp: number
    maxTemp: number
  }
  
  // 메타데이터
  createdAt: string
  updatedAt: string
}

// =============================================
// 6. 전역 상태 (통합된 GlobalState)
// =============================================

export interface UnifiedGlobalState {
  // 시스템 정보
  version: number
  appOption: string             // 기본 옵션
  
  // 프로젝트 관리
  projects: ProjectInfo[]
  currentProjectId: string | null
  
  // AHU 관리  
  ahus: AHUData[]
  currentAHUId: string | null
  
  // UI 상태
  ui: {
    activeForm: string | null    // 현재 활성 폼
    activeTab: number           // AHU 선정 폼 활성 탭
    sidebarCollapsed: boolean   // 사이드바 접힘 상태
  }
  
  // 임시 데이터 (계산 중 사용)
  tempData: {
    ahuCalculationResults: any   // AHU 계산 결과
    fanCalculationResults: any   // 팬 계산 결과
  }
  
  // 로딩 상태
  isLoaded: boolean
  
  // 시스템 설정
  settings: {
    autoSave: boolean           // 자동 저장 여부
    autoSaveInterval: number    // 자동 저장 간격 (ms)
    showCalculationDetails: boolean // 계산 상세 표시
    defaultUnits: {
      temperature: 'C' | 'F'
      pressure: 'Pa' | 'kPa' | 'inWG'
      airflow: 'CMH' | 'CFM'
    }
  }
}

// =============================================
// 7. localStorage 키 통합
// =============================================

/** localStorage 키 정의 */
export const STORAGE_KEYS = {
  // 메인 데이터 (공조기 선택 웹앱에 맞는 키명)
  MAIN_DATA: 'KDCSCOKR_AHUSELECTION',
  
  // 백업용 키들 (기존 키들과 호환성 유지)
  LEGACY_UI_STATE: 'KDCSCOKR_AHUSELECTION_UI',
  LEGACY_FAN_SELECTION: 'fan-selection-form-data'
} as const

// =============================================
// 8. 데이터 변환 유틸리티 타입
// =============================================

/** 기존 ProjectData를 ProjectInfo로 변환 */
export interface LegacyProjectData {
  id: string
  projectName: string
  buildingName: string
  location: string
  buildingType: string
  totalFloors: string
  basementFloors: string
  buildingArea: string
  outdoorConditionCode: string
  summerOutdoorTemp: string
  summerOutdoorHumidity: string
  winterOutdoorTemp: string
  winterOutdoorHumidity: string
  summerIndoorTemp: string
  summerIndoorHumidity: string
  winterIndoorTemp: string
  winterIndoorHumidity: string
  altitude?: string          // 고도 (선택적 - 구버전 호환성)
  summerDailyRange?: string  // 하계 일교차 (선택적 - 구버전 호환성)
  createdAt: string
  updatedAt: string
}

/** 기존 AHUData 레거시 버전 */
export interface LegacyAHUData {
  id: string
  projectId: string
  ahuNo: string
  systemName: string
  quantity: string
  location: string
  formData: Record<string, any>  // 타입이 불명확했던 기존 구조
  createdAt: string
  updatedAt: string
}

// =============================================
// 9. 유틸리티 함수 타입
// =============================================

/** 데이터 변환 함수들의 타입 */
export interface DataMigrationUtils {
  // 레거시 데이터 → 새 구조 변환
  migrateProjectData: (legacy: LegacyProjectData) => ProjectInfo
  migrateAHUData: (legacy: LegacyAHUData) => AHUData
  migrateFanData: (legacy: any) => FanSelectionData
  
  // 데이터 유효성 검사
  validateProjectInfo: (data: any) => data is ProjectInfo
  validateAHUData: (data: any) => data is AHUData
  validateFanSelectionData: (data: any) => data is FanSelectionData
  
  // 백업 및 복원
  createBackup: (state: UnifiedGlobalState) => string
  restoreFromBackup: (backup: string) => UnifiedGlobalState
  
  // 데이터 정리
  cleanupLegacyData: () => void
  mergeConflictingData: (existing: any, incoming: any) => any
}

// =============================================
// 10. 액션 타입 정의
// =============================================

export type UnifiedAction =
  | { type: 'LOAD_FROM_STORAGE'; payload: UnifiedGlobalState }
  | { type: 'SET_LOADED'; payload: boolean }
  
  // 프로젝트 관리
  | { type: 'ADD_PROJECT'; payload: ProjectInfo }
  | { type: 'UPDATE_PROJECT'; payload: ProjectInfo }
  | { type: 'DELETE_PROJECT'; payload: string }
  | { type: 'SET_CURRENT_PROJECT'; payload: string | null }
  
  // AHU 관리
  | { type: 'ADD_AHU'; payload: AHUData }
  | { type: 'UPDATE_AHU'; payload: AHUData }
  | { type: 'DELETE_AHU'; payload: string }
  | { type: 'SET_CURRENT_AHU'; payload: string | null }
  
  // AHU 폼 데이터 업데이트
  | { type: 'UPDATE_AHU_FORM_DATA'; payload: { ahuId: string; formData: Partial<AHUFormData> } }
  | { type: 'UPDATE_FAN_SELECTION_DATA'; payload: { ahuId: string; fanData: FanSelectionData } }
  
  // UI 상태
  | { type: 'SET_ACTIVE_FORM'; payload: string | null }
  | { type: 'SET_ACTIVE_TAB'; payload: number }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  
  // 임시 데이터
  | { type: 'SET_TEMP_CALCULATION_RESULTS'; payload: { type: 'ahu' | 'fan'; data: any } }
  | { type: 'CLEAR_TEMP_DATA' }
  
  // 시스템
  | { type: 'UPDATE_SETTINGS'; payload: Partial<UnifiedGlobalState['settings']> }
  | { type: 'RESET_ALL_DATA' }
  | { type: 'IMPORT_DATA'; payload: { projects: ProjectInfo[]; ahus: AHUData[] } }